import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useWarbandStore } from '../../stores/warbandStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { warbandService } from '../../services/warbandService';
import { SpellSchoolChip } from '../shared/SpellSchoolChip';

const createWarbandSchema = z.object({
  name: z.string().min(1, 'Party name is required'),
  description: z.string().optional(),
  schoolId: z.string().min(1, 'School is required'),
});

type CreateWarbandFormData = z.infer<typeof createWarbandSchema>;

export const CreateWarbandModal: React.FC = () => {
  const {
    ui,
    closeCreateWarbandModal,
    setWarbands,
    warbands,
    selectWarband,
    navigateToPage,
  } = useWarbandStore();
  const {
    schools,
    loadReferenceData,
    isLoading: loadingSchools,
    error: schoolsError,
    getSchoolNameById,
  } = useReferenceDataStore();

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const { control, handleSubmit, reset } = useForm<CreateWarbandFormData>({
    resolver: zodResolver(createWarbandSchema),
    defaultValues: {
      name: '',
      description: '',
      schoolId: '',
    },
  });

  React.useEffect(() => {
    if (!ui.createWarbandModalOpen) {
      return;
    }

    void loadReferenceData();
  }, [ui.createWarbandModalOpen, loadReferenceData]);

  const onSubmit = async (data: CreateWarbandFormData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await warbandService.create({
        name: data.name,
        description: data.description || '',
        schoolId: data.schoolId,
        baseId: null,
        gold: 400,
      } as any);

      setWarbands([...warbands, response.data]);
      selectWarband(response.data.id);
      navigateToPage('pregame');
      navigateToPage('manage');      
      reset();
      closeCreateWarbandModal();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create party';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    closeCreateWarbandModal();
  };

  return (
    <Dialog
      open={ui.createWarbandModalOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && (
          <Box sx={{ color: '#d32f2f', fontSize: '0.875rem', p: 1, backgroundColor: '#ffebee', borderRadius: 1 }}>
            {error}
          </Box>
        )}

        {schoolsError && (
          <Box sx={{ color: '#d32f2f', fontSize: '0.875rem', p: 1, backgroundColor: '#ffebee', borderRadius: 1 }}>
            {schoolsError}
          </Box>
        )}

        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error: fieldError } }) => (
            <TextField
              {...field}
              label="Party Name"
              placeholder="e.g., The Black Cloaks"
              fullWidth
              error={!!fieldError}
              helperText={fieldError?.message}
              disabled={loading || loadingSchools}
            />
          )}
        />

        <Controller
          name="schoolId"
          control={control}
          render={({ field, fieldState: { error: fieldError } }) => (
            <FormControl fullWidth error={!!fieldError} disabled={loading || loadingSchools || !!schoolsError}>
              <InputLabel id="create-warband-school-label">School of Magic</InputLabel>
              <Select
                {...field}
                labelId="create-warband-school-label"
                label="School of Magic"
                renderValue={(selected) => {
                  const selectedSchoolName = getSchoolNameById(selected);
                  return selectedSchoolName
                    ? selectedSchoolName
                    : String(selected);
                }}
              >
                <MenuItem value="" disabled>
                  Select a school
                </MenuItem>
                {schools.map((school) => (
                  <MenuItem key={school.id} value={school.id}>
                    <SpellSchoolChip label={school.school} />
                  </MenuItem>
                ))}
              </Select>
              {fieldError && (
                <Box sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5 }}>
                  {fieldError.message}
                </Box>
              )}
            </FormControl>
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Description"
              placeholder="Optional description"
              fullWidth
              multiline
              rows={3}
              disabled={loading || loadingSchools}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading || loadingSchools || !!schoolsError}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Party'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
